"use client";

import { useState, type FC, type FormEvent, type Ref } from "react";
import { ExclamationCircleIcon, SparklesIcon } from "@heroicons/react/24/outline";
import type { PractitionerSearchDto } from "cmw-client";
import { BorderBeam } from "@/components/border-beam";
import { filterCount } from "@/components/directory-filter-bar";
import { AiSearchIcon } from "@/components/icons";
import { Shimmer } from "@/components/shimmer";
import { TurnstileWidget, type TurnstileInstance } from "@/components/turnstile";
import { useTypingPlaceholder } from "@/hooks/use-typing-placeholder";
import { cn } from "@/lib/cn";

type LlmSearchInputProps = {
  onSearch: (query: string) => void;
  isSearching: boolean;
  result?: PractitionerSearchDto;
  /** A transport-level failure — the request itself never came back. */
  isError: boolean;
  turnstileRef: Ref<TurnstileInstance | undefined>;
};

const EXAMPLES = [
  "A female psychologist who speaks Arabic, for my teenager's anxiety",
  "A psychologist for my teenager's anxiety",
  "A male counsellor who speaks Urdu, by telehealth",
  "A female counsellor who works with couples",
  "A psychologist for trauma who brings faith into their work",
  "A female psychologist for pregnancy-related depression",
  "Support for adult ADHD",
];

const PILL =
  "flex h-52 items-center gap-8 rounded-circle border bg-neutral-white pr-6 pl-16 transition-colors sm:h-auto sm:border-2 sm:py-6 sm:pl-22";
const FIELD = "min-w-0 flex-1 text-16 text-neutral-black sm:py-12";
const PRIMARY_BUTTON =
  "flex size-40 shrink-0 items-center justify-center rounded-circle bg-brand-blue text-neutral-white transition-colors hover:bg-brand-dark-blue disabled:cursor-not-allowed sm:h-48 sm:w-auto sm:gap-8 sm:px-26 sm:text-16 sm:font-bold sm:disabled:opacity-60";
/** The searched sentence, shown back once it is no longer editable. */
const SubmittedQuery: FC<{ query: string }> = ({ query }) => (
  <span className={cn(FIELD, "truncate")}>{query}</span>
);

export const LlmSearchInput: FC<LlmSearchInputProps> = ({
  onSearch,
  isSearching,
  result,
  isError,
  turnstileRef,
}) => {
  const [value, setValue] = useState("");
  const [submitted, setSubmitted] = useState("");
  const [focused, setFocused] = useState(false);
  const placeholder = useTypingPlaceholder(EXAMPLES, focused || value.length > 0 || isSearching);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = value.trim();
    if (trimmed.length === 0 || isSearching) return;

    setSubmitted(trimmed);
    onSearch(trimmed);
  };

  /* A search that produced no filters is not a failure of the directory — it is a failure to
     understand the sentence. Either way the answer is the same: the filters below are open,
     so say so and get out of the way. */
  const applied = result?.interpreted ? filterCount(result.filters) : 0;
  const unusable = isError || result?.interpreted === false || (result !== undefined && applied === 0);

  const hasSettled = result !== undefined || isError;
  const isFailed = !isSearching && hasSettled && unusable;
  const submitLabel = hasSettled ? "Search again" : "Search";

  return (
    <section
      aria-label="Search by description"
      className="flex flex-col gap-12 rounded-medium border border-neutral-light-grey bg-neutral-white p-16 sm:gap-14 md:p-24"
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-8">
        <span className="flex items-center gap-8">
          <AiSearchIcon className="size-16 shrink-0 text-brand-blue" />
          <span className="text-12 font-bold tracking-[0.06em] text-neutral-black/80 uppercase">
            Search in your own words
          </span>
        </span>
        <span className="text-14 text-neutral-grey max-sm:first-letter:uppercase">
          we&apos;ll turn it into filters you can adjust
        </span>
      </div>

      <BorderBeam className="rounded-circle" state={isFailed ? "off" : "rest"}>
        {isSearching ? (
          <div className={cn(PILL, "border-tone-aqua-tint")}>
            <SubmittedQuery query={submitted} />
            <span className={cn(PRIMARY_BUTTON, "bg-tone-pool hover:bg-tone-pool")}>
              <span className="size-15 animate-spin rounded-circle border-2 border-neutral-white/45 border-t-neutral-white" />
              <span className="max-sm:hidden">Searching</span>
            </span>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className={cn(PILL, isFailed ? "border-tone-rose" : "border-tone-aqua-tint focus-within:border-brand-blue")}
          >
            <label className="sr-only" htmlFor="llm-filter-query">
              Describe who you are looking for
            </label>
            <input
              id="llm-filter-query"
              type="text"
              inputMode="search"
              enterKeyHint="search"
              autoComplete="off"
              value={value}
              onChange={(event) => setValue(event.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              placeholder={placeholder}
              maxLength={500}
              className={cn(FIELD, "bg-transparent outline-none placeholder:text-neutral-mid-grey")}
            />
            <button
              type="submit"
              disabled={value.trim().length === 0}
              aria-label={submitLabel}
              className={PRIMARY_BUTTON}
            >
              <AiSearchIcon className="size-20 sm:size-16" />
              <span className="max-sm:hidden">{submitLabel}</span>
            </button>
          </form>
        )}
      </BorderBeam>

      {isSearching && (
        <div role="status" className="flex items-center gap-8 px-4">
          <SparklesIcon className="size-16 shrink-0 text-brand-blue" />
          <Shimmer as="span" className="text-14 font-medium">
            Adding filters based on your request…
          </Shimmer>
        </div>
      )}

      {isFailed && (
        <div className="flex items-start gap-12 rounded-regular border border-tone-rose bg-tone-blush px-16 py-14 sm:px-18">
          <ExclamationCircleIcon className="mt-2 size-18 shrink-0 text-brand-red" />
          <div className="flex min-w-0 flex-1 flex-col gap-4">
            <span className="text-15 font-bold text-brand-red">We couldn&apos;t turn that into a search</span>
            <span className="text-14 text-neutral-black/70">
              Add a detail like gender, language or the concern — or set it yourself with the
              filters below.
            </span>
          </div>
        </div>
      )}

      <TurnstileWidget ref={turnstileRef} action="search" />
    </section>
  );
};
